<template>
  <article class="review-panel" :class="review ? 'review-panel--' + review.status.toLowerCase() : ''" aria-label="Revisión de la ficha" :aria-busy="busy">
    <p v-if="error" class="notification is-danger" role="alert">{{ error }}</p>
    <details v-if="review" class="review-panel__disclosure">
      <summary class="review-panel__header" title="Desplegar o contraer la revisión de ficha">
        <div class="review-panel__identity">
          <span class="review-panel__icon" aria-hidden="true"><i class="fa fa-clipboard-check"></i></span>
          <div class="review-panel__heading">
            <h2 class="review-panel__title">Revisión de ficha</h2>
            <div v-if="review.version" class="review-panel__meta">
              <span class="review-panel__version">Versión {{ review.version }}</span>
              <span class="review-panel__assignee">Responsable: <strong>{{ review.task && review.task.assigned_user_name || 'Pendiente de asignación' }}</strong></span>
            </div>
          </div>
        </div>
        <span class="review-panel__status"><span class="review-panel__status-dot" aria-hidden="true"></span>{{ labels[review.status] || review.status }}</span>
      </summary>
      <div v-if="review.observations" class="review-panel__observations">
        <span class="review-panel__caption">Observaciones</span>
        <p>{{ review.observations }}</p>
      </div>
      <details v-if="review.changes && review.changes.length" class="review-panel__changes">
        <summary>Consultar cambios enviados a revisión <span class="review-panel__count">{{ review.changes.length }}</span></summary>
        <div class="review-panel__table">
          <table class="table is-fullwidth">
            <thead><tr><th>Fecha y hora del envío</th><th>Campo</th><th>Antes</th><th>Después</th></tr></thead>
            <tbody>
              <tr v-for="(change, index) in review.changes" :key="index" :class="{ 'review-panel__change--latest': isLatestChange(change) }">
                <td><time v-if="change.submitted_at" :datetime="change.submitted_at">{{ formatDateTime(change.submitted_at) }}</time><span v-else>Fecha no disponible</span><span v-if="isLatestChange(change)" class="review-panel__latest">Más reciente</span></td>
                <td>{{ fields[change.field] || change.field }}</td><td>{{ display(change.before) }}</td><td>{{ display(change.after) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
      <div v-if="review.status === 'PENDIENTE' && review.can_assign" class="field review-panel__section">
        <label class="label" for="review-assignee">Asignar revisor autorizado</label>
        <div class="review-panel__assignment">
        <div class="select">
          <select id="review-assignee" v-model="reviewerId" :disabled="busy">
            <option :value="null">Seleccione un revisor</option>
            <option v-for="user in review.reviewers" :key="user.id" :value="user.id">{{ user.name }}</option>
          </select>
        </div>
        <button class="button" :disabled="busy || !reviewerId" @click="act('assign')">Asignar</button>
        </div>
        <p v-if="!review.reviewers.length" class="help">No hay revisores elegibles. Asigne el perfil “Revisor de fichas” a otro usuario activo desde Usuarios.</p>
      </div>
      <div v-if="review.status === 'PENDIENTE' && review.can_review" class="review-panel__section">
        <label class="label" for="review-observations">Observaciones</label>
        <textarea id="review-observations" v-model="observations" class="textarea" maxlength="2000" :disabled="busy" placeholder="Indique las correcciones necesarias o un comentario de aprobación"></textarea>
        <div class="buttons">
          <button v-if="review.task.status === 'ASIGNADA'" class="button" :disabled="busy" @click="act('start')">Iniciar revisión</button>
          <button class="button is-success" :disabled="busy" @click="act('approve')">Aprobar ficha</button>
          <button class="button is-warning" :disabled="busy || !observations.trim()" @click="act('corrections')">Solicitar correcciones</button>
        </div>
      </div>
    </details>
    <button v-else class="button" :disabled="busy" @click="load">{{ busy ? 'Cargando revisión…' : 'Actualizar revisión' }}</button>
  </article>
</template>

<script>
import { formatDateTime } from '@/utils/date'

export default {
  props: { companyId: { type: Number, required: true } },
  data: () => ({
    review: null, reviewerId: null, observations: '', busy: false, error: '',
    labels: { SIN_REVISION: 'Sin revisión previa', PENDIENTE: 'Pendiente de revisión', APROBADA: 'Aprobada', CORRECCIONES: 'Correcciones solicitadas' },
    fields: { name: 'Nombre', rut: 'RUT', dv: 'Dígito verificador', status: 'Estado del cliente', client_type: 'Tipo de cliente', phone: 'Teléfono', id_remu: 'Código REMU', id_conta: 'Código CONTA', start_activities: 'Inicio de actividades', tax_regime_id: 'Régimen tributario', biller_type_id: 'Tipo de facturador', organizational_type: 'Tipo de organización', requires_legal_fiscal_info: 'Requiere datos legales y fiscales' }
  }),
  watch: { companyId: { immediate: true, handler () { this.load() } } },
  methods: {
    formatDateTime,
    isLatestChange (change) {
      const latest = this.review.changes[0]
      return Boolean(change.submitted_at && latest && change.submitted_at === latest.submitted_at)
    },
    display (value) { return value === null || value === undefined || value === '' ? '—' : typeof value === 'boolean' ? (value ? 'Sí' : 'No') : value },
    async load () {
      this.busy = true
      try {
        const response = await this.$axios.get('company/review', { params: { company_id: this.companyId } })
        this.review = response.data.review
        this.reviewerId = this.review.task ? this.review.task.assigned_user_id : null
      } catch (error) {
        this.review = null
        this.error = 'No fue posible cargar la revisión.'
      } finally { this.busy = false }
    },
    async act (action) {
      this.busy = true
      this.error = ''
      try {
        await this.$axios.post('company/review', { company_id: this.companyId, version: this.review.version, action, reviewer_id: this.reviewerId, observations: this.observations })
        this.observations = ''
        this.$emit('changed')
        window.dispatchEvent(new Event('company-tasks-updated'))
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) || 'No fue posible completar la revisión.'
      } finally {
        await this.load()
      }
    }
  }
}
</script>

<style scoped>
.review-panel {
  --review-color: #62584c;
  --review-tint: #eee9e2;
  margin: 1rem 0;
  padding: 1.2rem;
  border: 1px solid rgba(104, 83, 57, 0.12);
  border-radius: 26px;
  background: linear-gradient(135deg, #fffcf7, #f7f1e9);
  box-shadow: 0 12px 30px rgba(40, 29, 18, 0.04);
  color: #352b21;
}
.review-panel--aprobada { --review-color: #2f6a43; --review-tint: #e4ecdf; }
.review-panel--pendiente { --review-color: #88621e; --review-tint: #f3e8ce; }
.review-panel--correcciones { --review-color: #984c34; --review-tint: #f4e2d9; }
.review-panel__header, .review-panel__identity, .review-panel__meta, .review-panel__assignment {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}
.review-panel__header { justify-content: space-between; flex-wrap: wrap; cursor: pointer; list-style: none; border-radius: 12px; }
.review-panel__header::-webkit-details-marker { display: none; }
.review-panel__header::marker { content: ''; }
.review-panel__identity, .review-panel__heading { min-width: 0; }
.review-panel__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: var(--review-tint);
  color: var(--review-color);
  font-size: 1.15rem;
}
.review-panel__title { margin: 0; color: #2e241b; font-size: 1rem; font-weight: 700; }
.review-panel__meta { flex-wrap: wrap; gap: 0.4rem 0.75rem; margin-top: 0.4rem; font-size: 0.8rem; color: #776b5d; }
.review-panel__version { padding: 0.15rem 0.55rem; border-radius: 999px; background: rgba(104, 83, 57, 0.07); white-space: nowrap; }
.review-panel__assignee { overflow-wrap: anywhere; }
.review-panel__assignee strong { color: #514334; font-weight: 600; }
.review-panel__status {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: var(--review-tint);
  color: var(--review-color);
  font-size: 0.78rem;
  font-weight: 700;
}
.review-panel__status-dot { width: 6px; height: 6px; flex-shrink: 0; border-radius: 50%; background: currentColor; }
.review-panel__observations { margin-top: 1rem; padding: 0.85rem 1rem; border: 1px solid rgba(104, 83, 57, 0.08); border-radius: 16px; background: rgba(255, 255, 255, 0.55); }
.review-panel__caption { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #80715f; }
.review-panel__observations p { margin: 0.3rem 0 0; font-size: 0.88rem; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
.review-panel__changes, .review-panel__section { margin: 1rem 0 0; padding-top: 1rem; border-top: 1px solid rgba(104, 83, 57, 0.12); }
.review-panel__changes summary { cursor: pointer; font-size: 0.82rem; font-weight: 600; }
.review-panel__count { display: inline-block; margin-left: 0.3rem; padding: 0.1rem 0.45rem; border-radius: 999px; background: #eee7dd; font-size: 0.72rem; }
.review-panel__table { overflow-x: auto; margin-top: 0.8rem; border: 1px solid #e9e0d4; border-radius: 14px; }
.review-panel .table { margin: 0; background: rgba(255, 255, 255, 0.6); color: #514334; font-size: 0.82rem; }
.review-panel th { background: #f0e9df; color: #625341; font-size: 0.75rem; }
.review-panel td, .review-panel th { padding: 0.7rem 0.85rem; border-color: #e9e0d4; }
.review-panel td { overflow-wrap: anywhere; min-width: 8rem; }
.review-panel__change--latest { background: #f0eadf; }
.review-panel__change--latest td:first-child { box-shadow: inset 3px 0 #93724a; }
.review-panel__latest { display: block; width: fit-content; margin-top: 0.35rem; padding: 0.15rem 0.5rem; border-radius: 999px; background: #e4d7c2; color: #634b2e; font-size: 0.68rem; font-weight: 700; }
.review-panel .label { margin-bottom: 0.5rem; color: #514334; font-size: 0.82rem; }
.review-panel__assignment { flex-wrap: wrap; gap: 0.5rem; }
.review-panel .select { max-width: 100%; }
.review-panel .select select, .review-panel .textarea { border-color: #ddd2c4; border-radius: 12px; background: #fffcf8; color: #352b21; font-family: inherit; font-size: 0.85rem; box-shadow: none; }
.review-panel .select select { max-width: 100%; }
.review-panel .select::after { border-color: #816747; }
.review-panel .textarea { min-height: 100px; }
.review-panel .help { margin-top: 0.55rem; color: #776b5d; }
.review-panel .buttons { gap: 0.5rem; margin: 0.8rem 0 0; }
.review-panel .button { height: auto; min-height: 2.5rem; margin: 0; padding: 0.55rem 1rem; border: 1px solid #ddd2c4; border-radius: 999px; background: #fffaf3; color: #514334; font-family: inherit; font-size: 0.8rem; font-weight: 600; white-space: normal; }
.review-panel .button.is-success { border-color: #3b6549; background: #3b6549; color: #fff; }
.review-panel .button.is-warning { border-color: #e3ce9f; background: #f3e8ce; color: #78571e; }
.review-panel .button:hover:not(:disabled) { filter: brightness(0.96); }
.review-panel .button:disabled { opacity: 0.5; }
.review-panel .button:focus-visible, .review-panel summary:focus-visible, .review-panel select:focus-visible, .review-panel textarea:focus-visible { outline: 2px solid #93724a; outline-offset: 3px; }
.review-panel .notification { padding: 0.8rem 1rem; border-radius: 14px; font-size: 0.85rem; }
@media (max-width: 640px) {
  .review-panel { padding: 1rem; border-radius: 22px; }
  .review-panel__identity { align-items: flex-start; }
  .review-panel__meta { align-items: flex-start; flex-direction: column; }
  .review-panel__assignment, .review-panel .buttons { align-items: stretch; flex-direction: column; }
  .review-panel__assignment .select, .review-panel__assignment select, .review-panel .button { width: 100%; }
}
</style>
